// Page navigation + mobile menu toggle.
//
// Ported from admin/index.php's inline <script> block (lines 2444-2472).
// navigate() reads state for the isOperator() check and lazy-loads page
// data by calling the inline forwarders (window.fetchGroups /
// window.fetchUsers) which delegate to the bundle's fetchGroupsBundle /
// fetchUsersBundle. The Activity Logs page (page 3) lazy-loads logs via
// fetchLogsBundle, and the Accounting page (page 6) lazy-loads
// renderAccountingGroupBundle — see the p === 3 and p === 6 arms below.

import { isOperator } from './auth';
import type { TimestopState } from './state';

// Switch the visible "page" div, highlight the active nav link, and
// lazy-load data for the Groups (1), Users (5) and Activity Logs (3)
// pages.
//
// Operators are blocked from opening Groups (1) or Users (5) — they get
// bounced to the Dashboard (0).
//
// Accepts both navigate(state, page) and navigate(page) — when the first
// argument is a number (HTML onclick calls navigate(0)), we read state
// from window.timestop.state.
export function navigate(stateOrPage: TimestopState | number, page?: number): void {
  let state: TimestopState;
  let p: number;
  if (typeof stateOrPage === 'number') {
    // HTML onclick style: navigate(0)
    state = (window as unknown as { timestop?: { state: TimestopState } }).timestop!.state;
    p = stateOrPage;
  } else {
    state = stateOrPage;
    p = page ?? 0;
  }
  // Operators are not allowed to open Groups (1) or Users (5).
  // Silently bounce them to the Dashboard.
  if (isOperator(state) && (p === 1 || p === 5)) {
    p = 0;
  }
  document.querySelectorAll('.page').forEach(el => el.classList.add('hidden'));
  const pageEl = document.getElementById(`page-${p}`);
  if (pageEl) pageEl.classList.remove('hidden');

  // Highlight active nav
  document.querySelectorAll('.nav-link').forEach(l => {
    l.classList.remove('text-emerald-600', 'border-b-2', 'border-emerald-600');
  });
  const active = document.getElementById(`nav-${p}`);
  if (active) active.classList.add('text-emerald-600', 'border-b-2', 'border-emerald-600');

  // Lazy-load page data — call the inline forwarders which delegate to
  // the bundle's fetchGroupsBundle / fetchUsersBundle. Phase 12.11
  // also lazy-loads logs for page 3 (Activity Logs) via fetchLogs.
  if (p === 1) {
    const fn = (window as { fetchGroups?: () => Promise<void> }).fetchGroups;
    if (fn) void fn();
  }
  if (p === 3) {
    // Activity Logs page: trigger a /api/logs fetch (no mid) and
    // repaint renderGlobalLogs once it lands. /api/logs is
    // role/group scoped server-side, so an operator's "Activity
    // Logs" page only sees their own group's terminals. We pull the
    // apiBase + csrfToken from window.timestop / window.API_BASE — the
    // inline index.php boot sets these module-scope lets, but the
    // bundle's navigation.ts runs in a TS context where the
    // bare names aren't available; window.* is the bridge.
    //
    // Force read: if state.globalLogs is empty/undefined, await
    // fetchLogs() and then call renderGlobalLogs() so the user
    // doesn't briefly see the "No activity logs yet." empty state
    // before the fetch lands. When globalLogs is already populated
    // (user navigated to Logs, then away, then back), skip the
    // fetch and just re-render — the data is still fresh enough
    // for the page-switch UX.
    const cfg = (window as { API_BASE?: string; timestop?: { state?: { csrfToken?: string | null } } });
    const apiBase = (cfg.API_BASE as string | undefined) ?? '/api';
    const csrfToken = (cfg.timestop && cfg.timestop.state && cfg.timestop.state.csrfToken) || null;
    const config = { apiBase, csrfToken };
    const hasLogs = Array.isArray(state.globalLogs) && state.globalLogs.length > 0;
    const fetchFn = (window as { fetchLogs?: (s: TimestopState, c: typeof config) => Promise<unknown[]> }).fetchLogs;
    const renderFn = (window as { renderGlobalLogs?: (s: TimestopState, full: boolean) => void }).renderGlobalLogs;
    if (hasLogs) {
      // Cache hit: re-render from existing state, no fetch.
      if (renderFn) renderFn(state, true);
    } else if (fetchFn) {
      // Force read: await the fetch so we can render with the
      // fresh rows in one user-perceived step, instead of
      // flickering the empty state for a frame.
      void (async () => {
        await fetchFn(state, config);
        if (renderFn) renderFn(state, true);
      })();
    } else if (renderFn) {
      // No fetch available — fall through to the empty render
      // (matches the pre-fix behaviour).
      renderFn(state, true);
    }
  }
  if (p === 5) {
    const fn = (window as { fetchUsers?: () => Promise<void> }).fetchUsers;
    if (fn) void fn();
  }
  if (p === 6) {
    // Accounting page: call the bundle's renderAccountingGroup(),
    // which paints the calendar + group picker into #accounting-group-root.
    // Same pattern as the p === 3 lazy-load above: read config from
    // window.timestop / window.API_BASE so the TS module can stay pure.
    const cfg = (window as { API_BASE?: string; timestop?: { state?: { csrfToken?: string | null } } });
    const apiBase = (cfg.API_BASE as string | undefined) ?? '/api';
    const csrfToken = (cfg.timestop && cfg.timestop.state && cfg.timestop.state.csrfToken) || null;
    const config = { apiBase, csrfToken };
    const fn = (window as { renderAccountingGroup?: (s: TimestopState, el: HTMLElement, c: typeof config) => Promise<void> }).renderAccountingGroup;
    if (fn) {
      const root = document.getElementById('accounting-group-root');
      if (root) void fn(state, root, config);
    }
  }
}

// Toggle the mobile hamburger menu open/closed and swap the icon SVG.
export function toggleMobileMenu(): void {
  const menu = document.getElementById('mobile-menu');
  if (!menu) return;
  menu.classList.toggle('hidden');
  const icon = document.getElementById('menu-icon');
  if (!icon) return;
  if (menu.classList.contains('hidden')) {
    icon.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />`;
  } else {
    icon.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6h12v12" />`;
  }
}